import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import ReactMarkdown from 'react-markdown';
import SwitchboardAIPanel from '../components/SwitchboardAIPanel';
import {
  Inbox, Mail, MessageSquare, Send, Archive, Trash2, Star,
  Search, Filter, Plus, RefreshCw, ChevronRight, ChevronDown, ChevronLeft,
  User, Users, Phone, Building, Tag, AlertCircle, CheckCircle2,
  Clock, FileText, Calendar, Zap, Bot, Globe, Loader2, X,
  MoreVertical, Reply, Forward, Paperclip, Languages, Settings2,
  Shield, UserCheck, Merge, Undo, Copy, ExternalLink, PanelRightClose,
  PanelRight, GripVertical, List, LayoutList, SortAsc, SortDesc,
  Sparkles, Play, Eye, EyeOff, Link2, ArrowUpRight, Edit2,
  MessageCircle, Linkedin, Hash
} from 'lucide-react';

const API = '';

// Provider config with launch URLs
const PROVIDER_CONFIG = {
  gmail: { 
    icon: Mail, 
    color: 'text-red-500', 
    bg: 'bg-red-50', 
    border: 'border-red-200',
    launchUrl: (email) => `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}`,
    label: 'Gmail'
  },
  slack: { 
    icon: MessageSquare, 
    color: 'text-purple-500', 
    bg: 'bg-purple-50', 
    border: 'border-purple-200',
    launchUrl: () => 'https://slack.com/',
    label: 'Slack'
  },
  telegram: { 
    icon: Send, 
    color: 'text-blue-500', 
    bg: 'bg-blue-50', 
    border: 'border-blue-200',
    launchUrl: (_, phone) => phone ? `https://t.me/${phone.replace(/[^0-9]/g, '')}` : 'https://telegram.org/',
    label: 'Telegram'
  },
  whatsapp: { 
    icon: Phone, 
    color: 'text-green-500', 
    bg: 'bg-green-50', 
    border: 'border-green-200',
    launchUrl: (_, phone) => phone ? `https://wa.me/${phone.replace(/[^0-9]/g, '')}` : 'https://web.whatsapp.com/',
    label: 'WhatsApp'
  },
  linkedin: { 
    icon: Linkedin, 
    color: 'text-sky-600', 
    bg: 'bg-sky-50', 
    border: 'border-sky-200',
    launchUrl: () => 'https://www.linkedin.com/messaging/',
    label: 'LinkedIn'
  },
  line: {
    icon: MessageCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    launchUrl: () => 'https://line.me/',
    label: 'LINE'
  },
  internal: { 
    icon: Zap, 
    color: 'text-violet-500', 
    bg: 'bg-violet-50', 
    border: 'border-violet-200',
    launchUrl: () => '#',
    label: 'Orka'
  }
};

// Triage category config
const TRIAGE_CONFIG = {
  needs_reply: { label: 'Needs Reply', icon: Reply, color: 'text-amber-600', bg: 'bg-amber-50' },
  needs_task: { label: 'Needs Task', icon: CheckCircle2, color: 'text-blue-600', bg: 'bg-blue-50' },
  awaiting: { label: 'Awaiting', icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
  fyi: { label: 'FYI', icon: FileText, color: 'text-slate-600', bg: 'bg-slate-50' },
  newsletter: { label: 'Newsletter', icon: Mail, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  receipt: { label: 'Receipt', icon: FileText, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  invoice: { label: 'Invoice', icon: FileText, color: 'text-orange-600', bg: 'bg-orange-50' },
  spam: { label: 'Spam', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
  already_handled: { label: 'Handled', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' }
};

// Message fluff patterns to collapse
const FLUFF_PATTERNS = [
  /^--\s*$/m,  // Email signature delimiter
  /^_{3,}/m,   // Underline separator
  /^-{3,}/m,   // Dash separator
  /Sent from my (iPhone|iPad|Android|Samsung|Pixel)/i,
  /Get Outlook for/i,
  /This email and any attachments/i,
  /CONFIDENTIAL/i,
  /DISCLAIMER/i,
  /Please consider the environment/i,
  /unsubscribe/i
];

// Extract core message by removing fluff
const extractCoreMessage = (body) => {
  if (!body) return { core: '', fluff: '' };
  
  let core = body;
  let fluff = '';
  
  // Find first fluff pattern
  for (const pattern of FLUFF_PATTERNS) {
    const match = core.match(pattern);
    if (match) {
      const idx = core.indexOf(match[0]);
      if (idx > 50) { // Only if there's substantial content before
        fluff = core.substring(idx);
        core = core.substring(0, idx).trim();
        break;
      }
    }
  }
  
  return { core, fluff };
};

// Rich Markdown Renderer Component
// Rich Content Renderer - supports markdown and interactive elements
const RichMessage = ({ content, className }) => {
  const [executedBlocks, setExecutedBlocks] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Parse special interactive patterns
  const parseInteractiveContent = (text) => {
    if (!text) return [];
    
    const elements = [];
    let remaining = text;
    let index = 0;
    
    // Pattern: [button:Label:action:params] for interactive buttons
    // Pattern: [collapse:Title]...[/collapse] for collapsible sections
    // Pattern: [widget:type:data] for embedded widgets
    // Pattern: [runnable]code[/runnable] for executable code
    
    const patterns = [
      // Interactive button: [button:Click Me:navigate:/dashboard]
      {
        regex: /\[button:([^:]+):([^:]+):([^\]]*)\]/g,
        render: (match, label, action, params, key) => (
          <Button
            key={key}
            size="sm"
            variant="outline"
            className="mx-1 my-1"
            onClick={() => handleButtonAction(action, params)}
          >
            {label}
          </Button>
        )
      },
      // Task button: [task:Task Description]
      {
        regex: /\[task:([^\]]+)\]/g,
        render: (match, taskDesc, key) => (
          <Button
            key={key}
            size="sm"
            variant="secondary"
            className="mx-1 my-1 bg-violet-100 hover:bg-violet-200 text-violet-700"
            onClick={() => toast.info(`Task created: ${taskDesc}`)}
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Create Task: {taskDesc.slice(0, 30)}{taskDesc.length > 30 ? '...' : ''}
          </Button>
        )
      },
      // Calendar button: [calendar:Event Name:2024-03-15]
      {
        regex: /\[calendar:([^:]+):([^\]]+)\]/g,
        render: (match, eventName, date, key) => (
          <Button
            key={key}
            size="sm"
            variant="secondary"
            className="mx-1 my-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
            onClick={() => toast.info(`Add to calendar: ${eventName} on ${date}`)}
          >
            <Calendar className="w-3 h-3 mr-1" />
            {eventName}
          </Button>
        )
      },
      // Link card: [link:Title:URL:Description]
      {
        regex: /\[link:([^:]+):([^:]+):([^\]]*)\]/g,
        render: (match, title, url, desc, key) => (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block my-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="font-medium text-sm flex items-center gap-2">
              <ExternalLink className="w-3 h-3 text-violet-500" />
              {title}
            </div>
            {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
          </a>
        )
      },
      // Progress indicator: [progress:75:Status message]
      {
        regex: /\[progress:(\d+):([^\]]*)\]/g,
        render: (match, percent, message, key) => (
          <div key={key} className="my-2 p-3 bg-muted/50 rounded-lg">
            <div className="flex justify-between text-xs mb-1">
              <span>{message}</span>
              <span className="font-medium">{percent}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-violet-500 rounded-full orka-transition-colors"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )
      },
      // Info box: [info:Message]
      {
        regex: /\[info:([^\]]+)\]/g,
        render: (match, message, key) => (
          <div key={key} className="my-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-blue-700">{message}</span>
          </div>
        )
      },
      // Warning box: [warning:Message]
      {
        regex: /\[warning:([^\]]+)\]/g,
        render: (match, message, key) => (
          <div key={key} className="my-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-amber-700">{message}</span>
          </div>
        )
      },
      // Success box: [success:Message]
      {
        regex: /\[success:([^\]]+)\]/g,
        render: (match, message, key) => (
          <div key={key} className="my-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-emerald-700">{message}</span>
          </div>
        )
      }
    ];
    
    // Simple approach: render markdown first, then process interactive elements
    return text;
  };
  
  const handleButtonAction = (action, params) => {
    switch (action) {
      case 'navigate':
        window.location.href = params;
        break;
      case 'copy':
        navigator.clipboard.writeText(params);
        toast.success('Copied to clipboard');
        break;
      case 'open':
        window.open(params, '_blank');
        break;
      default:
        toast.info(`Action: ${action}`);
    }
  };
  
  // Process content for interactive elements before markdown
  const processContent = (text) => {
    if (!text) return '';
    
    // Replace interactive patterns with placeholder or rendered HTML
    let processed = text;
    
    // Convert [button:...] to markdown-friendly format
    processed = processed.replace(/\[button:([^:]+):([^:]+):([^\]]*)\]/g, '**[$1]($3)**');
    processed = processed.replace(/\[task:([^\]]+)\]/g, '📋 **Task:** $1');
    processed = processed.replace(/\[calendar:([^:]+):([^\]]+)\]/g, '📅 **Event:** $1 on $2');
    processed = processed.replace(/\[progress:(\d+):([^\]]*)\]/g, '⏳ **$2** ($1%)');
    processed = processed.replace(/\[info:([^\]]+)\]/g, '> ℹ️ $1');
    processed = processed.replace(/\[warning:([^\]]+)\]/g, '> ⚠️ $1');
    processed = processed.replace(/\[success:([^\]]+)\]/g, '> ✅ $1');
    
    return processed;
  };
  
  const processedContent = processContent(content);
  
  return (
    <div className={cn("prose prose-sm max-w-none dark:prose-invert", className)}>
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="my-2 ml-4 list-disc">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 ml-4 list-decimal">{children}</ol>,
          li: ({ children }) => <li className="my-0.5">{children}</li>,
          code: ({ inline, children, className: codeClassName }) => {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const language = match ? match[1] : '';
            
            if (inline) {
              return <code className="px-1 py-0.5 bg-muted rounded text-sm">{children}</code>;
            }
            
            return (
              <div className="relative group my-2">
                <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 bg-background/80"
                    onClick={() => {
                      navigator.clipboard.writeText(String(children));
                      toast.success('Code copied!');
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                {language && (
                  <div className="absolute left-2 top-2 text-[10px] text-muted-foreground font-mono uppercase">
                    {language}
                  </div>
                )}
                <pre className="p-3 pt-7 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-sm">
                  <code>{children}</code>
                </pre>
              </div>
            );
          },
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">
              {children}
              <ExternalLink className="w-3 h-3" />
            </a>
          ),
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-violet-300 pl-3 italic text-muted-foreground my-2">{children}</blockquote>
          ),
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
          th: ({ children }) => <th className="px-3 py-2 text-left font-medium">{children}</th>,
          td: ({ children }) => <td className="px-3 py-2 border-t">{children}</td>,
          hr: () => <hr className="my-4 border-muted" />,
          h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-bold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

const SwitchboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, setTheme } = useTheme();
  
  // View state
  const [activeView, setActiveView] = useState('inbox'); // inbox, contacts, channels, rules
  const [breadcrumb, setBreadcrumb] = useState([{ label: 'Switchboard', view: 'inbox' }]);
  const [inboxWidth, setInboxWidth] = useState(380);
  const [aiPanelOpen, setAiPanelOpen] = useState(true);
  const [compactView, setCompactView] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [gmailAccounts, setGmailAccounts] = useState([]);
  
  // Selection states
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [expandedFluff, setExpandedFluff] = useState({});
  const [replyFromAccount, setReplyFromAccount] = useState('');
  
  // Filter/Sort states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterReadStatus, setFilterReadStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modal states
  const [triageModalOpen, setTriageModalOpen] = useState(false);
  const [triageResults, setTriageResults] = useState(null);
  const [triageRunning, setTriageRunning] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  
  // AI Composer states
  const [composerBody, setComposerBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null); // {file, preview, data, mimetype, filename}
  const fileInputRef = useRef(null);
  
  // New contact/rule form
  const [newContact, setNewContact] = useState({ display_name: '', primary_email: '', primary_phone: '', company: '' });
  const [newRule, setNewRule] = useState({ name: '', conditions: {}, actions: {} });
  
  // Email thread chat view
  const [chatViewMessages, setChatViewMessages] = useState(null);
  const [parsingThread, setParsingThread] = useState(false);
  const [chatViewActive, setChatViewActive] = useState(false);
  
  // Contact merge
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const resizeRef = useRef(null);

  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/switchboard/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error('Error fetching stats:', err); }
  }, []);
  
  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterProvider !== 'all') params.append('provider', filterProvider);
      
      const res = await fetch(`${API}/api/switchboard/threads?limit=500&${params}`, { credentials: 'include' });
      if (res.ok) {
        let data = await res.json();
        
        // Apply client-side filters
        if (filterReadStatus === 'unread') data = data.filter(t => t.is_unread_orka);
        if (filterReadStatus === 'read') data = data.filter(t => !t.is_unread_orka);
        if (filterProvider !== 'all') data = data.filter(t => t.primary_provider === filterProvider);
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          data = data.filter(t => 
            t.subject?.toLowerCase().includes(q) ||
            t.snippet?.toLowerCase().includes(q) ||
            t.contact_name?.toLowerCase().includes(q) ||
            t.group_name?.toLowerCase().includes(q) ||
            t.from_email?.toLowerCase().includes(q) ||
            t.slack_channel_name?.toLowerCase().includes(q)
          );
        }
        
        // Sort by datetime - ALL messages sorted together regardless of provider
        data.sort((a, b) => {
          const aVal = new Date(a.last_message_at || 0).getTime();
          const bVal = new Date(b.last_message_at || 0).getTime();
          return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
        });
        
        setThreads(data);
      }
    } catch (err) { console.error('Error fetching threads:', err); }
    finally { setLoading(false); }
  }, [filterCategory, filterProvider, filterReadStatus, searchQuery, sortOrder]);
  
  const fetchThreadMessages = useCallback(async (threadId) => {
    try {
      const res = await fetch(`${API}/api/switchboard/threads/${threadId}/messages`, { credentials: 'include' });
      if (res.ok) setThreadMessages(await res.json());
    } catch (err) { console.error('Error fetching messages:', err); }
  }, []);
  
  const fetchContacts = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const res = await fetch(`${API}/api/switchboard/contacts${params}`, { credentials: 'include' });
      if (res.ok) setContacts(await res.json());
    } catch (err) { console.error('Error fetching contacts:', err); }
  }, [searchQuery]);
  
  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/switchboard/channels`, { credentials: 'include' });
      if (res.ok) setChannels(await res.json());
    } catch (err) { console.error('Error fetching channels:', err); }
  }, []);
  
  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/switchboard/rules`, { credentials: 'include' });
      if (res.ok) setRules(await res.json());
    } catch (err) { console.error('Error fetching rules:', err); }
  }, []);
  
  const fetchGmailAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/oauth/gmail/accounts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setGmailAccounts(data.accounts || []);
      }
    } catch (err) { console.error('Error fetching Gmail accounts:', err); }
  }, []);
  
  // Handle media file selection
  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:xxx;base64, prefix
      setPendingMedia({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        data: base64,
        mimetype: file.type,
        filename: file.name
      });
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // Reset so same file can be re-selected
  };

  // Fetch live WhatsApp messages from the WhatsApp service
  const fetchWhatsAppLiveMessages = useCallback(async (jid) => {
    try {
      const res = await fetch(`${API}/api/whatsapp/messages/${encodeURIComponent(jid)}?limit=100`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.messages) {
          // Transform to our message format
          const transformed = data.messages.map(m => ({
            message_id: m.id,
            provider: 'whatsapp',
            direction: m.is_from_me ? 'outbound' : 'inbound',
            is_from_me: m.is_from_me,
            from_name: m.is_from_me ? 'You' : (m.sender_name || m.from_name || m.from),
            body_cleaned: m.body || '',
            body_snippet: (m.body || '').substring(0, 100),
            received_at: m.timestamp,
            sent_at: m.is_from_me ? m.timestamp : null,
            timestamp: m.timestamp,
            has_media: m.has_media,
            media: m.media || null,
            media_info: m.media ? { mimetype: m.media.mimetype, filename: m.media.filename } : null,
            msg_type: m.type,
            is_group: data.is_group,
            sender_name: m.sender_name
          }));
          setThreadMessages(transformed);
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp messages:', err);
    }
  }, []);

  // Send message function
  const sendMessage = async () => {
    if ((!composerBody.trim() && !pendingMedia) || !selectedThread) return;
    
    setSendingMessage(true);
    
    try {
      const provider = selectedThread.primary_provider;
      let endpoint = '';
      let payload = {};
      
      if (provider === 'whatsapp') {
        // Send via WhatsApp (with optional media)
        const phoneNumber = selectedThread.whatsapp_jid?.replace('@s.whatsapp.net', '').replace('@g.us', '');
        endpoint = `${API}/api/whatsapp/send`;
        payload = {
          jid: selectedThread.whatsapp_jid,
          phone_number: phoneNumber,
          message: composerBody
        };
        if (pendingMedia) {
          payload.media = {
            data: pendingMedia.data,
            mimetype: pendingMedia.mimetype,
            filename: pendingMedia.filename
          };
        }
      } else if (provider === 'slack') {
        // Send via Slack (with optional file)
        endpoint = `${API}/api/slack/send`;
        payload = {
          channel_id: selectedThread.slack_channel_id,
          text: composerBody
        };
        if (pendingMedia) {
          payload.file = {
            data: pendingMedia.data,
            mimetype: pendingMedia.mimetype,
            filename: pendingMedia.filename
          };
        }
      } else if (provider === 'gmail') {
        // Send via Gmail (future implementation)
        toast.info('Gmail sending coming soon');
        setSendingMessage(false);
        return;
      } else {
        toast.error('Cannot send via this channel');
        setSendingMessage(false);
        return;
      }
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const result = await res.json();
        if (result.success) {
          toast.success('Message sent!');
          setComposerBody('');
          setPendingMedia(null);
          // Refresh messages - use live fetch for WhatsApp
          if (provider === 'whatsapp' && selectedThread.whatsapp_jid) {
            setTimeout(() => fetchWhatsAppLiveMessages(selectedThread.whatsapp_jid), 1000);
          } else {
            fetchThreadMessages(selectedThread.thread_id);
          }
        } else {
          toast.error(result.error || 'Failed to send');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch (err) {
      console.error('Send error:', err);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchStats();
    fetchThreads();
    fetchChannels();
    fetchGmailAccounts();
    fetchContacts(); // Load contacts for name display
  }, [fetchStats, fetchThreads, fetchChannels, fetchGmailAccounts, fetchContacts]);
  
  // Load data based on active view
  useEffect(() => {
    if (activeView === 'contacts') fetchContacts();
    if (activeView === 'rules') fetchRules();
  }, [activeView, fetchContacts, fetchRules]);
  
  // Thread selection - set default reply-from account
  useEffect(() => {
    if (selectedThread) {
      // For WhatsApp threads, fetch live messages from the WhatsApp service
      if (selectedThread.primary_provider === 'whatsapp' && selectedThread.whatsapp_jid) {
        fetchWhatsAppLiveMessages(selectedThread.whatsapp_jid);
      } else {
        fetchThreadMessages(selectedThread.thread_id);
      }
    }
  }, [selectedThread, fetchThreadMessages, fetchWhatsAppLiveMessages]);
  
  // When messages load, set reply-from to the account that received the last message
  useEffect(() => {
    if (threadMessages.length > 0) {
      const lastInbound = [...threadMessages].reverse().find(m => m.direction === 'inbound');
      if (lastInbound?.synced_from_account) {
        setReplyFromAccount(lastInbound.synced_from_account);
      } else if (gmailAccounts.length > 0) {
        setReplyFromAccount(gmailAccounts[0].email);
      }
    }
  }, [threadMessages, gmailAccounts]);
  
  // Resize handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newWidth = Math.max(280, Math.min(900, e.clientX - 64)); // 64 = sidebar width
      setInboxWidth(newWidth);
    };
    
    const handleMouseUp = () => setIsDragging(false);
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Navigation helpers
  const navigateToView = (view, label) => {
    setActiveView(view);
    if (view === 'inbox') {
      setBreadcrumb([{ label: 'Switchboard', view: 'inbox' }]);
    } else {
      setBreadcrumb([
        { label: 'Switchboard', view: 'inbox' },
        { label, view }
      ]);
    }
  };
  
  // Thread actions
  const markRead = async (threadId) => {
    try {
      await fetch(`${API}/api/switchboard/threads/${threadId}/read`, { method: 'POST', credentials: 'include' });
      fetchThreads();
      fetchStats();
    } catch (err) { toast.error('Failed to mark as read'); }
  };
  
  const archiveThread = async (threadId) => {
    try {
      await fetch(`${API}/api/switchboard/threads/${threadId}/archive`, { method: 'POST', credentials: 'include' });
      setSelectedThread(null);
      fetchThreads();
      fetchStats();
      toast.success('Thread archived');
    } catch (err) { toast.error('Failed to archive'); }
  };
  
  const markSpam = async (threadId) => {
    try {
      await fetch(`${API}/api/switchboard/threads/${threadId}/spam`, { method: 'POST', credentials: 'include' });
      setSelectedThread(null);
      fetchThreads();
      fetchStats();
      toast.success('Marked as spam');
    } catch (err) { toast.error('Failed to mark spam'); }
  };
  
  // AI Triage - uses the dedicated auto-triage endpoint
  const runAutoTriage = async () => {
    setTriageRunning(true);
    setTriageResults(null);
    
    try {
      const unreadThreads = threads.filter(t => t.is_unread_orka && !t.triage_category);
      if (unreadThreads.length === 0) {
        toast.info('No untriaged messages');
        setTriageRunning(false);
        return;
      }
      
      // Batch in groups of 10 (API limit)
      const allItems = [];
      const batches = [];
      for (let i = 0; i < Math.min(unreadThreads.length, 30); i += 10) {
        batches.push(unreadThreads.slice(i, i + 10));
      }
      
      for (const batch of batches) {
        const messages = batch.map(t => ({
          message_id: t.thread_id,
          channel: t.primary_provider || 'email',
          from_name: t.contact_name || t.group_name || 'Unknown',
          subject: t.subject || '',
          body: t.snippet || '',
          date: t.last_message_at || ''
        }));
        
        const res = await fetch(`${API}/api/ai/switchboard/auto-triage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ messages })
        });
        
        if (res.ok) {
          const data = await res.json();
          const results = data.results || [];
          for (const r of results) {
            const thread = batch.find(t => t.thread_id === r.id);
            allItems.push({
              thread_id: r.id,
              subject: thread?.subject || r.subject || '',
              suggested_category: r.category,
              reason: r.reason || '',
              suggested_action: r.suggested_action || '',
              priority: r.priority || 3,
              current_category: thread?.triage_category
            });
          }
        }
      }
      
      // Build summary
      const categories = {};
      allItems.forEach(item => {
        categories[item.suggested_category] = (categories[item.suggested_category] || 0) + 1;
      });
      
      setTriageResults({ total: allItems.length, categories, items: allItems });
    } catch (err) {
      toast.error('Triage failed');
    } finally {
      setTriageRunning(false);
    }
  };
  
  // Triage per-item notes state
  const [triageNotes, setTriageNotes] = useState({});
  const [triageOverrides, setTriageOverrides] = useState({});
  
  const applyTriageItem = async (item, approved) => {
    const notes = triageNotes[item.thread_id] || '';
    const overrideCategory = triageOverrides[item.thread_id];
    const finalCategory = approved ? item.suggested_category : (overrideCategory || item.suggested_category);
    
    try {
      // Apply category to thread
      await fetch(`${API}/api/switchboard/threads/${item.thread_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ triage_category: finalCategory })
      });
      
      // Save feedback as triage rule if there are notes or a disagreement
      if (notes || !approved) {
        const ruleText = approved 
          ? `Messages about "${item.subject}" should be categorized as ${finalCategory}. ${notes}`
          : `Messages like "${item.subject}" should be categorized as ${finalCategory}, not ${item.suggested_category}. Reason: ${notes}`;
        
        await fetch(`${API}/api/ai/switchboard/triage-rule`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            rule: ruleText,
            message_id: item.thread_id,
            original_category: item.suggested_category,
            user_category: finalCategory,
            notes
          })
        });
      }
      
      // Remove from results
      setTriageResults(prev => {
        if (!prev) return prev;
        const newItems = prev.items.filter(i => i.thread_id !== item.thread_id);
        const newCategories = {};
        newItems.forEach(i => { newCategories[i.suggested_category] = (newCategories[i.suggested_category] || 0) + 1; });
        return { ...prev, items: newItems, categories: newCategories };
      });
      
      toast.success(approved ? 'Approved & applied' : 'Overridden & saved as rule');
    } catch (err) { toast.error('Failed to apply triage'); }
  };
  
  const applyAllTriage = async () => {
    if (!triageResults?.items) return;
    for (const item of triageResults.items) {
      await applyTriageItem(item, true);
    }
    setTriageModalOpen(false);
    fetchThreads();
  };
  
  // Contact actions
  const createContact = async () => {
    if (!newContact.display_name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const res = await fetch(`${API}/api/switchboard/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newContact)
      });
      if (res.ok) {
        toast.success('Contact created');
        setNewContact({ display_name: '', primary_email: '', primary_phone: '', company: '' });
        setContactModalOpen(false);
        fetchContacts();
        fetchStats();
      }
    } catch (err) { toast.error('Failed to create contact'); }
  };
  
  const toggleWhitelist = async (contactId, enabled) => {
    try {
      await fetch(`${API}/api/switchboard/contacts/${contactId}/whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ global_enabled: enabled })
      });
      fetchContacts();
      toast.success(enabled ? 'Contact whitelisted' : 'Whitelist removed');
    } catch (err) { toast.error('Failed to update whitelist'); }
  };
  
  // Rule actions
  const saveRule = async () => {
    if (!newRule.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    try {
      const res = await fetch(`${API}/api/switchboard/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newRule)
      });
      if (res.ok) {
        toast.success('Rule created');
        setNewRule({ name: '', conditions: {}, actions: {} });
        setRuleModalOpen(false);
        fetchRules();
      }
    } catch (err) { toast.error('Failed to create rule'); }
  };
  
  // Channel launch helper
  const launchChannel = (provider, email, phone) => {
    const config = PROVIDER_CONFIG[provider];
    if (config?.launchUrl) {
      const url = config.launchUrl(email, phone);
      if (url !== '#') window.open(url, '_blank');
    }
  };
  
  // Connect channel (redirect to settings if not integrated)
  const connectChannel = (provider) => {
    const integrated = channels.some(c => c.provider === provider);
    if (integrated) {
      toast.info(`${provider} is already connected`);
    } else {
      navigate('/settings?tab=integrations');
    }
  };

  // Email thread chat view parser
  const parseEmailThread = async () => {
    if (!threadMessages?.length) return;
    setParsingThread(true);
    try {
      // Combine all email bodies in the thread
      const fullBody = threadMessages
        .map(m => `From: ${m.from_name || m.sender_name || m.from_email || 'Unknown'}\nDate: ${m.received_at || m.timestamp || ''}\n\n${m.body_cleaned || m.body_snippet || ''}`)
        .join('\n\n---\n\n');
      
      const res = await fetch(`${API}/api/ai/switchboard/parse-email-thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email_body: fullBody })
      });
      
      if (res.ok) {
        const data = await res.json();
        setChatViewMessages(data.messages || []);
        setChatViewActive(true);
      } else {
        toast.error('Failed to parse email thread');
      }
    } catch { toast.error('Error parsing thread'); }
    finally { setParsingThread(false); }
  };

  // Fetch merge suggestions
  const fetchMergeSuggestions = async () => {
    setMergeLoading(true);
    try {
      const res = await fetch(`${API}/api/switchboard/contacts/merge/suggestions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMergeSuggestions(Array.isArray(data) ? data : []);
      }
    } catch { toast.error('Failed to load merge suggestions'); }
    finally { setMergeLoading(false); }
  };

  // Execute contact merge
  const executeMerge = async (primaryId, mergeIds) => {
    try {
      const res = await fetch(`${API}/api/switchboard/contacts/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ primary_id: primaryId, merge_ids: mergeIds })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`Merged ${data.merged_count} contact(s)`);
          fetchContacts();
          fetchMergeSuggestions();
        } else {
          toast.error(data.error || 'Merge failed');
        }
      }
    } catch { toast.error('Merge failed'); }
  };

  // Dismiss merge suggestion
  const dismissMerge = (idx) => {
    setMergeSuggestions(prev => prev.filter((_, i) => i !== idx));
  };


  // Render thread item
  const renderThreadItem = (thread) => {
    const providerConfig = PROVIDER_CONFIG[thread.primary_provider] || PROVIDER_CONFIG.internal;
    const ProviderIcon = providerConfig.icon;
    const triageConfig = TRIAGE_CONFIG[thread.triage_category];
    const isSelected = selectedThread?.thread_id === thread.thread_id;
    
    // Find contact name from contacts list or use group_name
    const contact = contacts.find(c => c.contact_id === thread.contact_id);
    const displayName = contact?.display_name || thread.group_name || thread.contact_name || 'Unknown';
    
    // Get the source account for this thread - look up in channels array
    const sourceChannel = channels.find(ch => ch.account_id === thread.channel_account_id);
    const sourceAccountEmail = sourceChannel?.provider_email || null;
    
    // For Slack messages from channels (not DMs), show channel name
    const slackChannelName = thread.primary_provider === 'slack' && thread.slack_channel_name ? 
      `[#${thread.slack_channel_name}]` : '';
    
    // For email, show the from email address
    const fromEmail = thread.primary_provider === 'gmail' ? thread.from_email : null;
    
    // Format timestamp with full datetime for better visibility
    const formatDateTime = (dateStr) => {
      if (!dateStr) return 'No date';
      
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
        
        const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        if (isToday) {
          return timeStr;
        } else if (isYesterday) {
          return `Yesterday ${timeStr}`;
        } else {
          return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + timeStr;
        }
      } catch (e) {
        return 'No date';
      }
    };
    
    return (
      <div
        key={thread.thread_id}
        onClick={() => { setSelectedThread(thread); markRead(thread.thread_id); setChatViewActive(false); setChatViewMessages(null); }}
        className={cn(
          "border-b cursor-pointer hover:bg-muted/50 transition-colors",
          compactView ? "p-2" : "p-3",
          isSelected && "bg-violet-50 border-l-2 border-l-violet-500",
          thread.is_unread_orka && !isSelected && "bg-blue-50/50 border-l-2 border-l-blue-400"
        )}
        data-testid={`thread-item-${thread.thread_id}`}
      >
        <div className="flex items-start gap-2">
          <div className={cn("rounded-full flex items-center justify-center flex-shrink-0", providerConfig.bg, compactView ? "w-6 h-6" : "w-8 h-8")}>
            <ProviderIcon className={cn(providerConfig.color, compactView ? "w-3 h-3" : "w-4 h-4")} />
          </div>
          <div className="flex-1 min-w-0">
            {/* Row 1: Name + Provider + DateTime */}
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <span className={cn("font-medium truncate", thread.is_unread_orka && "font-semibold", compactView ? "text-xs" : "text-sm")}>
                  {displayName}
                </span>
                {/* Slack channel indicator */}
                {slackChannelName && (
                  <span className="text-purple-600 text-[10px] font-medium flex-shrink-0">{slackChannelName}</span>
                )}
              </div>
              {/* DateTime - always visible */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", providerConfig.bg, providerConfig.color)}>
                  {providerConfig.label}
                </span>
                <span className={cn("text-slate-600 whitespace-nowrap font-medium tabular-nums", compactView ? "text-[10px]" : "text-xs")}>
                  {formatDateTime(thread.last_message_at)}
                </span>
              </div>
            </div>
            {/* From email - full address on own row for Gmail */}
            {fromEmail && (
              <div className="text-[11px] text-muted-foreground truncate" title={fromEmail}>
                {fromEmail}
              </div>
            )}
            
            {!compactView && thread.subject && (
              <div className="text-sm truncate font-medium">{thread.subject}</div>
            )}
            <div className={cn("text-muted-foreground truncate", compactView ? "text-[10px]" : "text-xs")}>
              {thread.snippet || 'No preview available'}
            </div>
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {/* Gmail source indicator - which inbox received this */}
              {thread.primary_provider === 'gmail' && sourceAccountEmail && !compactView && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-medium" title={`Received by ${sourceAccountEmail}`}>
                  <Mail className="w-2.5 h-2.5" />
                  to: {sourceAccountEmail}
                </span>
              )}
              {triageConfig && !compactView && (
                <Badge variant="secondary" className={cn("text-xs", triageConfig.bg, triageConfig.color)}>
                  {triageConfig.label}
                </Badge>
              )}
              {thread.is_spam && (
                <Badge variant="destructive" className="text-xs">Spam</Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render message with chat bubble style (WhatsApp/Telegram-like)
  const renderMessage = (msg, idx) => {
    const { core, fluff } = extractCoreMessage(msg.body_cleaned);
    const isExpanded = expandedFluff[msg.message_id];
    
    // Determine if message is from the user
    const userEmails = gmailAccounts.map(a => a.email?.toLowerCase());
    const msgFrom = (msg.from_email || '').toLowerCase();
    const isFromMe = msg.direction === 'outbound' || 
      msg.is_from_me ||
      userEmails.some(e => e && msgFrom.includes(e)) ||
      (msg.synced_from_account && msgFrom === msg.synced_from_account?.toLowerCase());
    
    const messageBody = core || msg.body_snippet || msg.body_cleaned || '';
    const senderInitial = msg.from_name?.charAt(0)?.toUpperCase() || msg.sender_name?.charAt(0)?.toUpperCase() || '?';
    const senderName = msg.from_name || msg.sender_name || msg.from_email || 'Unknown';
    
    const timeStr = (msg.received_at || msg.sent_at || msg.timestamp) ? new Date(msg.received_at || msg.sent_at || msg.timestamp).toLocaleString([], { 
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    }) : '';

    // Channel origin badge
    const provider = msg.provider || selectedThread?.primary_provider || 'email';
    const channelBadge = {
      whatsapp: { label: 'WhatsApp', color: 'bg-green-100 text-green-700', icon: '💬' },
      slack: { label: 'Slack', color: 'bg-purple-100 text-purple-700', icon: '#' },
      gmail: { label: 'Email', color: 'bg-blue-100 text-blue-700', icon: '✉' },
      email: { label: 'Email', color: 'bg-blue-100 text-blue-700', icon: '✉' },
    }[provider] || { label: provider, color: 'bg-slate-100 text-slate-600', icon: '•' };

    // Media rendering helper
    const renderMedia = () => {
      if (!msg.has_media && !msg.media && !msg.media_info) return null;
      const mediaInfo = msg.media || msg.media_info;
      if (!mediaInfo) {
        return (
          <div className={cn("flex items-center gap-2 p-2 rounded-lg mt-1", isFromMe ? "bg-violet-400/30" : "bg-slate-50 border border-slate-200")}>
            <Paperclip className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs italic">Media attachment</span>
          </div>
        );
      }
      const mime = mediaInfo.mimetype || '';
      if (mime.startsWith('image/') && mediaInfo.data) {
        return (
          <div className="mt-1.5 rounded-lg overflow-hidden max-w-[300px]">
            <img src={`data:${mime};base64,${mediaInfo.data}`} alt="Shared image" className="w-full h-auto rounded-lg" />
          </div>
        );
      }
      if (mime.startsWith('video/') && mediaInfo.data) {
        return (
          <div className="mt-1.5 rounded-lg overflow-hidden max-w-[300px]">
            <video controls className="w-full h-auto rounded-lg" src={`data:${mime};base64,${mediaInfo.data}`} />
          </div>
        );
      }
      if (mime.startsWith('audio/') && mediaInfo.data) {
        return (
          <div className="mt-1.5">
            <audio controls className="w-full max-w-[250px]" src={`data:${mime};base64,${mediaInfo.data}`} />
          </div>
        );
      }
      return (
        <div className={cn("flex items-center gap-2 p-2 rounded-lg mt-1", isFromMe ? "bg-violet-400/30" : "bg-slate-50 border border-slate-200")}>
          <Paperclip className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs">{mediaInfo.filename || 'File attachment'}</span>
        </div>
      );
    };

    return (
      <div key={msg.message_id || idx} className={cn("flex gap-2 mb-4", isFromMe ? "flex-row-reverse" : "flex-row")}
        data-testid={`message-bubble-${msg.message_id || idx}`}>
        {/* Avatar */}
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-1",
          isFromMe ? "bg-violet-500" : "bg-gradient-to-br from-slate-500 to-slate-700"
        )}>
          {isFromMe ? 'Me' : senderInitial}
        </div>
        
        {/* Bubble */}
        <div className={cn("max-w-[75%] min-w-[180px]", isFromMe ? "items-end" : "items-start")}>
          {/* Sender name + time + channel badge */}
          <div className={cn("flex items-center gap-2 mb-1 px-1 flex-wrap", isFromMe ? "flex-row-reverse" : "flex-row")}>
            <span className="text-xs font-medium text-foreground">
              {isFromMe ? 'You' : senderName}
            </span>
            <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-medium", channelBadge.color)}>
              {channelBadge.icon} {channelBadge.label}
            </span>
            <span className="text-[10px] text-muted-foreground">{timeStr}</span>
          </div>
          
          {/* Message bubble */}
          <div className={cn(
            "rounded-2xl px-4 py-2.5 shadow-sm relative",
            isFromMe 
              ? "bg-violet-500 text-white rounded-tr-sm" 
              : "bg-white border border-slate-200 rounded-tl-sm"
          )}>
            {/* From email for Gmail (subtle, inside bubble) */}
            {msg.from_email && !isFromMe && msg.provider === 'gmail' && (
              <div className="text-[10px] opacity-60 mb-1 truncate">{msg.from_email}</div>
            )}
            
            {/* Group message sender for group chats */}
            {msg.is_group && msg.sender_name && !isFromMe && (
              <div className="text-[10px] font-semibold opacity-70 mb-0.5">{msg.sender_name}</div>
            )}
            
            {/* Message content */}
            {messageBody ? (
              <div className={cn(isFromMe && "prose-invert")}>
                <RichMessage content={messageBody} className={cn("text-sm", isFromMe && "[&_*]:text-white [&_a]:text-violet-200 [&_code]:text-violet-200 [&_code]:bg-violet-600/30")} />
              </div>
            ) : (
              !msg.has_media && <p className="text-sm italic opacity-60">No content</p>
            )}

            {/* Media content */}
            {renderMedia()}
            
            {/* Show more / signature */}
            {fluff && (
              <div className={cn("mt-1.5 border-t pt-1.5", isFromMe ? "border-violet-400/30" : "border-slate-200")}>
                <button
                  onClick={() => setExpandedFluff(prev => ({ ...prev, [msg.message_id]: !isExpanded }))}
                  className={cn("text-[11px] flex items-center gap-1", isFromMe ? "text-violet-200 hover:text-white" : "text-muted-foreground hover:text-foreground")}
                >
                  {isExpanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {isExpanded ? 'Hide' : 'More...'}
                </button>
                {isExpanded && (
                  <div className={cn("mt-1 text-[11px] whitespace-pre-wrap", isFromMe ? "text-violet-200" : "text-muted-foreground")}>
                    {fluff}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render contact card with channel links
  const renderContactCard = (contact) => {
    const connectedChannels = ['gmail', 'whatsapp', 'telegram', 'linkedin', 'line', 'slack'];
    
    return (
      <Card key={contact.contact_id} className="hover:shadow-md orka-transition-colors" data-testid={`contact-${contact.contact_id}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
              {contact.display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-lg">{contact.display_name}</span>
                {contact.is_staff_member && <Badge variant="outline">Staff</Badge>}
                {contact.is_whitelisted_global && <Badge className="bg-emerald-500">Whitelisted</Badge>}
              </div>
              
              {contact.company && (
                <div className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                  <Building className="w-3 h-3" />
                  {contact.company}
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 text-sm mb-3">
                {contact.primary_email && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Mail className="w-3 h-3" />{contact.primary_email}
                  </span>
                )}
                {contact.primary_phone && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Phone className="w-3 h-3" />{contact.primary_phone}
                  </span>
                )}
              </div>
              
              {/* Channel launch icons */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground mr-2">Message via:</span>
                {connectedChannels.map(provider => {
                  const config = PROVIDER_CONFIG[provider];
                  if (!config) return null;
                  const IconComp = config.icon;
                  const isConnected = channels.some(c => c.provider === provider);
                  
                  return (
                    <button
                      key={provider}
                      onClick={() => isConnected 
                        ? launchChannel(provider, contact.primary_email, contact.primary_phone)
                        : connectChannel(provider)
                      }
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center orka-transition-colors",
                        isConnected 
                          ? `${config.bg} hover:scale-110 cursor-pointer` 
                          : "bg-muted/50 opacity-40 cursor-not-allowed"
                      )}
                      title={isConnected ? `Open ${config.label}` : `Connect ${config.label} first`}
                    >
                      <IconComp className={cn("w-4 h-4", isConnected ? config.color : "text-muted-foreground")} />
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              <Switch
                checked={contact.is_whitelisted_global}
                onCheckedChange={(checked) => toggleWhitelist(contact.contact_id, checked)}
              />
              <span className="text-xs text-muted-foreground">Orka Access</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <PageLayout 
      theme={theme}
      setTheme={setTheme}
      title="Switchboard"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant={activeView === 'inbox' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => navigateToView('inbox', 'Inbox')}
          >
            <Inbox className="w-4 h-4 mr-1" />Inbox
          </Button>
          <Button
            variant={activeView === 'contacts' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => navigateToView('contacts', 'Contacts')}
          >
            <UserCheck className="w-4 h-4 mr-1" />Contacts
          </Button>
          <Button
            variant={activeView === 'channels' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => navigateToView('channels', 'Channels')}
          >
            <Link2 className="w-4 h-4 mr-1" />Channels
          </Button>
          <Button
            variant={activeView === 'rules' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => navigateToView('rules', 'Rules')}
          >
            <Settings2 className="w-4 h-4 mr-1" />Rules
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <Button variant="outline" size="sm" onClick={() => setTriageModalOpen(true)}>
            <Sparkles className="w-4 h-4 mr-1" />Auto Triage
          </Button>
          <Button variant="ghost" size="icon" onClick={() => { fetchStats(); fetchThreads(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="switchboard-page">
        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {activeView === 'inbox' && (
            <>
              {/* Inbox List */}
              <div style={{ width: inboxWidth }} className="border-r flex flex-col bg-background flex-shrink-0">
                {/* Inbox Header */}
                <div className="px-3 py-2 border-b bg-gradient-to-r from-violet-50 to-background">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Inbox className="w-4 h-4 text-violet-600" />
                      <h2 className="font-semibold text-sm">Inbox</h2>
                      {threads.length > 0 && (
                        <Badge variant="secondary" className="text-xs">{threads.length}</Badge>
                      )}
                    </div>
                    {stats && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="font-medium text-violet-600">{stats.unread_orka || 0} unread</span>
                        <span className="text-amber-600">{stats.needs_reply || 0} awaiting</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Filters */}
                <div className="p-2 border-b space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search messages..."
                        className="pl-8 h-8 text-sm"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCompactView(!compactView)}
                      title={compactView ? "Normal view" : "Compact view"}
                    >
                      {compactView ? <LayoutList className="w-4 h-4" /> : <List className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  <div className="flex gap-1 flex-wrap items-center">
                    {/* Provider filter */}
                    <Select value={filterProvider} onValueChange={setFilterProvider}>
                      <SelectTrigger className="h-7 text-xs w-28">
                        <SelectValue placeholder="Provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Channels</SelectItem>
                        <SelectItem value="gmail">Gmail</SelectItem>
                        <SelectItem value="slack">Slack</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterReadStatus} onValueChange={setFilterReadStatus}>
                      <SelectTrigger className="h-7 text-xs w-20">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="unread">Unread</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="h-7 text-xs w-24">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {Object.entries(TRIAGE_CONFIG).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center gap-1 ml-auto">
                      <span className="text-[10px] text-muted-foreground">Sort:</span>
                      <Button
                        variant={sortOrder === 'desc' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setSortOrder('desc')}
                      >
                        Newest
                      </Button>
                      <Button
                        variant={sortOrder === 'asc' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setSortOrder('asc')}
                      >
                        Oldest
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Thread List */}
                <ScrollArea className="flex-1">
                  {loading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : threads.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500 opacity-50" />
                      <p className="font-medium">Inbox Zero!</p>
                      <p className="text-sm">All caught up</p>
                    </div>
                  ) : (
                    threads.map(renderThreadItem)
                  )}
                </ScrollArea>
              </div>
              
              {/* Resize Handle */}
              <div
                ref={resizeRef}
                className="w-1 bg-border hover:bg-violet-400 cursor-col-resize transition-colors flex-shrink-0"
                onMouseDown={() => setIsDragging(true)}
              />
              
              {/* Conversation Viewer */}
              <div className="flex-1 flex flex-col min-w-0">
                {!selectedThread ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Inbox className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Select a conversation to view</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Thread header */}
                    <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{selectedThread.group_name || 'Conversation'}</h3>
                        {selectedThread.subject && <p className="text-sm text-muted-foreground truncate">{selectedThread.subject}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {/* Email thread chat view toggle */}
                        {selectedThread.primary_provider === 'gmail' && threadMessages.length > 0 && (
                          <Button
                            variant={chatViewActive ? "default" : "ghost"}
                            size="sm"
                            className="h-8 text-xs gap-1"
                            onClick={() => {
                              if (chatViewActive) {
                                setChatViewActive(false);
                                setChatViewMessages(null);
                              } else {
                                parseEmailThread();
                              }
                            }}
                            disabled={parsingThread}
                            data-testid="chat-view-toggle"
                          >
                            {parsingThread ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                            {chatViewActive ? 'Original' : 'Chat View'}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => archiveThread(selectedThread.thread_id)} title="Archive">
                          <Archive className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Star">
                          <Star className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => markSpam(selectedThread.thread_id)} title="Spam">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Messages - chat style */}
                    <ScrollArea className="flex-1">
                      <div className="p-4 bg-slate-50/80 min-h-full">
                        {chatViewActive && chatViewMessages ? (
                          // AI-parsed chat view for email threads
                          <>
                            <div className="text-center mb-4">
                              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200">
                                <Sparkles className="w-3 h-3 mr-1" />AI Chat View - {chatViewMessages.length} messages extracted
                              </Badge>
                            </div>
                            {chatViewMessages.map((msg, idx) => {
                              const userEmails = gmailAccounts.map(a => a.email?.toLowerCase());
                              const isFromMe = userEmails.some(e => msg.sender?.toLowerCase().includes(e)) || msg.sender?.toLowerCase().includes('you') || msg.sender?.toLowerCase().includes('me');
                              return (
                                <div key={idx} className={cn("flex gap-2 mb-4", isFromMe ? "flex-row-reverse" : "flex-row")} data-testid={`chat-msg-${idx}`}>
                                  <div className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 mt-1",
                                    isFromMe ? "bg-violet-500" : "bg-gradient-to-br from-slate-500 to-slate-700"
                                  )}>
                                    {isFromMe ? 'Me' : (msg.sender?.charAt(0)?.toUpperCase() || '?')}
                                  </div>
                                  <div className={cn("max-w-[75%] min-w-[180px]")}>
                                    <div className={cn("flex items-center gap-2 mb-1 px-1", isFromMe ? "flex-row-reverse" : "flex-row")}>
                                      <span className="text-xs font-medium">{isFromMe ? 'You' : (msg.sender || 'Unknown')}</span>
                                      {msg.date && <span className="text-[10px] text-muted-foreground">{msg.date}</span>}
                                    </div>
                                    <div className={cn(
                                      "rounded-2xl px-4 py-2.5 shadow-sm",
                                      isFromMe ? "bg-violet-500 text-white rounded-tr-sm" : "bg-white border border-slate-200 rounded-tl-sm"
                                    )}>
                                      <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        ) : threadMessages.length === 0 ? (
                          <div className="text-center text-muted-foreground py-8">
                            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>No messages in this thread</p>
                          </div>
                        ) : (
                          threadMessages.map((msg, idx) => renderMessage(msg, idx))
                        )}
                      </div>
                    </ScrollArea>
                    
                    {/* Reply Composer */}
                    <div className="p-4 border-t flex-shrink-0">
                      {/* Channel-specific reply selector */}
                      {selectedThread?.primary_provider === 'gmail' && gmailAccounts.length > 0 && (
                        <div className="flex items-center gap-2 mb-2 text-sm">
                          <span className="text-muted-foreground">From:</span>
                          <Select value={replyFromAccount} onValueChange={setReplyFromAccount}>
                            <SelectTrigger className="h-8 w-auto min-w-[200px]">
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {gmailAccounts.map((acc) => (
                                <SelectItem key={acc.email} value={acc.email}>
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-3 h-3 text-red-500" />
                                    {acc.email}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedThread?.primary_provider === 'whatsapp' && (
                        <div className="flex items-center gap-2 mb-2 text-sm">
                          <MessageCircle className="w-4 h-4 text-green-500" />
                          <span className="text-muted-foreground">
                            Reply via WhatsApp to {selectedThread.contact_name || selectedThread.whatsapp_jid?.replace('@s.whatsapp.net', '')}
                          </span>
                        </div>
                      )}
                      {selectedThread?.primary_provider === 'slack' && (
                        <div className="flex items-center gap-2 mb-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-purple-500" />
                          <span className="text-muted-foreground">
                            Reply via Slack to #{selectedThread.contact_name || selectedThread.slack_channel_id}
                          </span>
                        </div>
                      )}
                      <Textarea
                        value={composerBody}
                        onChange={(e) => setComposerBody(e.target.value)}
                        placeholder={`Type your ${selectedThread?.primary_provider === 'whatsapp' ? 'WhatsApp' : selectedThread?.primary_provider === 'slack' ? 'Slack' : ''} reply...`}
                        className="min-h-[80px] mb-2"
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      />
                      {/* Media preview */}
                      {pendingMedia && (
                        <div className="mb-2 flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                          {pendingMedia.preview ? (
                            <img src={pendingMedia.preview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded bg-slate-200 flex items-center justify-center">
                              <Paperclip className="w-5 h-5 text-slate-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{pendingMedia.filename}</p>
                            <p className="text-[10px] text-muted-foreground">{pendingMedia.mimetype}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPendingMedia(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleMediaSelect}
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                      />
                      <div className="flex justify-between">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="attach-media-btn">
                            <Paperclip className="w-4 h-4 mr-1" />Attach
                          </Button>
                          <Button variant="ghost" size="sm"><Languages className="w-4 h-4 mr-1" />Translate</Button>
                        </div>
                        <Button 
                          onClick={sendMessage}
                          disabled={(!composerBody.trim() && !pendingMedia) || sendingMessage}
                          data-testid="send-message-btn"
                        >
                          {sendingMessage ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending...</>
                          ) : (
                            <><Send className="w-4 h-4 mr-1" />Send</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* AI Assistant Panel */}
              <SwitchboardAIPanel
                conversationContext={selectedThread ? {
                  channel: selectedThread.primary_provider || 'email',
                  subject: selectedThread.subject || selectedThread.display_name,
                  from_name: selectedThread.display_name,
                  from_email: selectedThread.email,
                  thread_id: selectedThread.thread_id,
                  messages: threadMessages.map(m => ({
                    sender_name: m.sender_name || m.from_name || m.from || 'Unknown',
                    body: m.body || m.text || m.snippet || '',
                    text: m.text || m.body || '',
                    from: m.from || m.sender_name || '',
                    date: m.date || m.timestamp || '',
                    timestamp: m.timestamp || m.date || ''
                  }))
                } : null}
                messages={threadMessages}
                onAction={(action) => {
                  if (action.type === 'push_to_composer' && action.data?.text) {
                    setComposerBody(prev => prev + (prev ? '\n\n' : '') + action.data.text);
                    toast.success('Added to composer');
                  }
                }}
                onNextMessage={() => {
                  if (!selectedThread || !threads.length) return;
                  const idx = threads.findIndex(t => t.thread_id === selectedThread.thread_id);
                  if (idx >= 0 && idx < threads.length - 1) {
                    setSelectedThread(threads[idx + 1]);
                  } else {
                    toast.info('No more messages');
                  }
                }}
                onPrevMessage={() => {
                  if (!selectedThread || !threads.length) return;
                  const idx = threads.findIndex(t => t.thread_id === selectedThread.thread_id);
                  if (idx > 0) {
                    setSelectedThread(threads[idx - 1]);
                  }
                }}
              />
              {!aiPanelOpen && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-20 z-10"
                  onClick={() => setAiPanelOpen(true)}
                  data-testid="open-ai-panel"
                >
                  <PanelRight className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
          
          {activeView === 'contacts' && (
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Contacts</h2>
                    <p className="text-muted-foreground">Manage your contacts and Orka communication permissions</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchMergeSuggestions} disabled={mergeLoading} data-testid="find-duplicates-btn">
                      {mergeLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Merge className="w-4 h-4 mr-1" />}
                      Find Duplicates
                    </Button>
                    <Button onClick={() => setContactModalOpen(true)} data-testid="add-contact-btn">
                      <Plus className="w-4 h-4 mr-1" />Add Contact
                    </Button>
                  </div>
                </div>
                
                {/* Merge Suggestions */}
                {mergeSuggestions.length > 0 && (
                  <div className="mb-6 border rounded-lg border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="merge-suggestions">
                    <div className="flex items-center gap-2">
                      <Merge className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-sm text-amber-700">Duplicate Contacts Found ({mergeSuggestions.length})</span>
                    </div>
                    {mergeSuggestions.map((suggestion, idx) => {
                      const [c1, c2] = suggestion.contacts || [];
                      if (!c1 || !c2) return null;
                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border" data-testid={`merge-suggestion-${idx}`}>
                          <div className="flex-1 flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 text-sm font-semibold">
                                {c1.display_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{c1.display_name}</p>
                                <p className="text-[10px] text-muted-foreground">{c1.primary_email || c1.primary_phone || ''}</p>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground px-2">
                              <ArrowUpRight className="w-3 h-3 inline" /> {Math.round(suggestion.confidence * 100)}% match
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-semibold">
                                {c2.display_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-medium">{c2.display_name}</p>
                                <p className="text-[10px] text-muted-foreground">{c2.primary_email || c2.primary_phone || ''}</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground max-w-48 truncate" title={suggestion.reason}>
                            {suggestion.reason}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => dismissMerge(idx)} data-testid={`dismiss-merge-${idx}`}>
                              Skip
                            </Button>
                            <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" onClick={() => executeMerge(c1.contact_id, [c2.contact_id])}
                              data-testid={`merge-contacts-${idx}`}>
                              <Merge className="w-3 h-3 mr-0.5" />Merge
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); fetchContacts(); }}
                      placeholder="Search contacts..."
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  {contacts.map(renderContactCard)}
                  {contacts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-medium">No contacts yet</p>
                      <p className="text-sm">Add contacts to manage communications</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {activeView === 'channels' && (
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Connected Channels</h2>
                    <p className="text-muted-foreground">Manage your communication channels</p>
                  </div>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  {channels.map((channel) => {
                    const config = PROVIDER_CONFIG[channel.provider] || PROVIDER_CONFIG.internal;
                    const IconComp = config.icon;
                    return (
                      <Card key={channel.account_id} className={cn("hover:shadow-md orka-transition-colors", config.border)}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", config.bg)}>
                              <IconComp className={cn("w-6 h-6", config.color)} />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">{channel.display_name}</div>
                              <div className="text-sm text-muted-foreground">{channel.provider_email || channel.provider}</div>
                              {channel.last_sync_at && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Last sync: {new Date(channel.last_sync_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                            <Badge variant={channel.status === 'active' ? 'default' : 'secondary'}>
                              {channel.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                
                {channels.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Globe className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="font-medium">No channels connected</p>
                    <p className="text-sm mb-4">Connect Gmail, Slack, or other services to get started</p>
                    <Button onClick={() => navigate('/settings?tab=integrations')}>
                      Go to Integrations
                    </Button>
                  </div>
                )}
                
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4">Connect More Channels</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(PROVIDER_CONFIG).filter(([k]) => k !== 'internal').map(([provider, config]) => {
                      const IconComp = config.icon;
                      const isConnected = channels.some(c => c.provider === provider);
                      return (
                        <Button
                          key={provider}
                          variant={isConnected ? "secondary" : "outline"}
                          className="gap-2"
                          onClick={() => connectChannel(provider)}
                          disabled={isConnected}
                        >
                          <IconComp className={cn("w-4 h-4", config.color)} />
                          {config.label}
                          {isConnected && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeView === 'rules' && (
            <div className="flex-1 p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Triage Rules</h2>
                    <p className="text-muted-foreground">Automate message categorization with rules</p>
                  </div>
                  <Button onClick={() => { setEditingRule(null); setRuleModalOpen(true); }}>
                    <Plus className="w-4 h-4 mr-1" />Add Rule
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {rules.map((rule) => (
                    <Card key={rule.rule_id} className="hover:shadow-md orka-transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{rule.name}</span>
                              <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                                {rule.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            {rule.description && <p className="text-sm text-muted-foreground mt-1">{rule.description}</p>}
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {Object.entries(rule.conditions || {}).map(([key, val]) => (
                                <Badge key={key} variant="outline" className="text-xs">
                                  {key}: {String(val)}
                                </Badge>
                              ))}
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              {Object.entries(rule.actions || {}).map(([key, val]) => (
                                <Badge key={key} variant="secondary" className="text-xs">
                                  {key}: {String(val)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{rule.match_count || 0} matches</span>
                            <Button variant="ghost" size="icon" onClick={() => { setEditingRule(rule); setRuleModalOpen(true); }}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {rules.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Settings2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-medium">No rules configured</p>
                      <p className="text-sm">Create rules to automatically triage your messages</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Auto Triage Modal */}
      <Dialog open={triageModalOpen} onOpenChange={setTriageModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Auto Triage
            </DialogTitle>
            <DialogDescription>
              AI analyzes your unread messages and suggests categories. Approve, override, or add notes that become persistent rules.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-2">
            {!triageResults && !triageRunning && (
              <div className="text-center py-8">
                <Bot className="w-16 h-16 mx-auto mb-4 text-violet-500 opacity-50" />
                <p className="mb-2 text-sm text-muted-foreground">{threads.filter(t => t.is_unread_orka && !t.triage_category).length} untriaged messages found</p>
                <Button onClick={runAutoTriage} data-testid="start-triage-btn">
                  <Play className="w-4 h-4 mr-1" />Start Triage
                </Button>
              </div>
            )}
            
            {triageRunning && (
              <div className="text-center py-8">
                <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-violet-500" />
                <p>Analyzing messages with AI...</p>
              </div>
            )}
            
            {triageResults && (
              <div className="space-y-4">
                {/* Category summary */}
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(triageResults.categories).map(([cat, count]) => {
                    const config = TRIAGE_CONFIG[cat];
                    return (
                      <div key={cat} className={cn("px-3 py-1.5 rounded-lg flex items-center gap-2", config?.bg || 'bg-muted')}>
                        <span className={cn("text-lg font-bold", config?.color || 'text-foreground')}>{count}</span>
                        <span className="text-xs">{config?.label || cat}</span>
                      </div>
                    );
                  })}
                </div>
                
                {triageResults.items.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                    <p className="text-sm">All items reviewed!</p>
                  </div>
                )}
                
                {/* Per-item review */}
                <div className="space-y-3">
                  {triageResults.items.map((item, idx) => {
                    const config = TRIAGE_CONFIG[item.suggested_category];
                    const overrideVal = triageOverrides[item.thread_id];
                    return (
                      <div key={item.thread_id} className="border rounded-lg p-3 space-y-2" data-testid={`triage-item-${idx}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.subject || 'No subject'}</p>
                            {item.reason && <p className="text-xs text-muted-foreground mt-0.5">{item.reason}</p>}
                            {item.suggested_action && <p className="text-xs text-violet-600 italic mt-0.5">{item.suggested_action}</p>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="text-[10px]">P{item.priority}</Badge>
                            <Badge className={cn("text-xs", config?.bg, config?.color)}>{config?.label || item.suggested_category}</Badge>
                          </div>
                        </div>
                        
                        {/* Notes + Override row */}
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Input
                              value={triageNotes[item.thread_id] || ''}
                              onChange={e => setTriageNotes(prev => ({...prev, [item.thread_id]: e.target.value}))}
                              placeholder="Add notes (becomes a rule)..."
                              className="h-7 text-xs"
                            />
                          </div>
                          <Select value={overrideVal || ''} onValueChange={v => setTriageOverrides(prev => ({...prev, [item.thread_id]: v}))}>
                            <SelectTrigger className="h-7 text-xs w-32">
                              <SelectValue placeholder="Override..." />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(TRIAGE_CONFIG).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyTriageItem(item, false)}
                            disabled={!overrideVal && !triageNotes[item.thread_id]} data-testid={`triage-disagree-${idx}`}>
                            Override
                          </Button>
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => applyTriageItem(item, true)}
                            data-testid={`triage-approve-${idx}`}>
                            <CheckCircle2 className="w-3 h-3 mr-0.5" />Approve
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTriageModalOpen(false); setTriageNotes({}); setTriageOverrides({}); }}>Close</Button>
            {triageResults?.items?.length > 0 && (
              <Button onClick={applyAllTriage} data-testid="apply-all-triage-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" />Approve All ({triageResults.items.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add/Edit Rule Modal */}
      <Dialog open={ruleModalOpen} onOpenChange={setRuleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Triage Rule'}</DialogTitle>
            <DialogDescription>
              Create rules to automatically categorize incoming messages.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Rule Name</label>
              <Input
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                placeholder="e.g., Newsletter Filter"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Conditions</label>
              <div className="space-y-2 mt-1">
                <Input
                  placeholder="From contains (e.g., newsletter@)"
                  value={newRule.conditions.from_contains || ''}
                  onChange={(e) => setNewRule({ ...newRule, conditions: { ...newRule.conditions, from_contains: e.target.value } })}
                />
                <Input
                  placeholder="Subject contains"
                  value={newRule.conditions.subject_contains || ''}
                  onChange={(e) => setNewRule({ ...newRule, conditions: { ...newRule.conditions, subject_contains: e.target.value } })}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Actions</label>
              <div className="space-y-2 mt-1">
                <Select
                  value={newRule.actions.category || ''}
                  onValueChange={(v) => setNewRule({ ...newRule, actions: { ...newRule.actions, category: v } })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Set category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIAGE_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newRule.actions.auto_archive || false}
                    onCheckedChange={(v) => setNewRule({ ...newRule, actions: { ...newRule.actions, auto_archive: v } })}
                  />
                  <span className="text-sm">Auto-archive</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleModalOpen(false)}>Cancel</Button>
            <Button onClick={saveRule}>Save Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Contact Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={newContact.display_name}
                onChange={(e) => setNewContact({ ...newContact, display_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                value={newContact.primary_email}
                onChange={(e) => setNewContact({ ...newContact, primary_email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={newContact.primary_phone}
                onChange={(e) => setNewContact({ ...newContact, primary_phone: e.target.value })}
                placeholder="+1234567890"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Company</label>
              <Input
                value={newContact.company}
                onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                placeholder="Acme Inc"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactModalOpen(false)}>Cancel</Button>
            <Button onClick={createContact}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default SwitchboardPage;
